import { useState, useRef, useEffect, KeyboardEvent, ClipboardEvent } from 'react';
import { Box, TextField, Typography } from '@mui/material';

interface PhoneInputProps {
  value: string;
  onChange: (value: string) => void;
  onComplete?: (value: string) => void;
  error?: boolean;
  helperText?: string;
  label?: string;
  disabled?: boolean;
}

/**
 * PhoneInput Component
 * 
 * Accepts Egyptian phone format: 01\d{9}
 * Example: 01234567890
 * 
 * - First two digits (0, 1) are fixed
 * - Remaining 9 digits are editable
 * - Single-digit inputs with underline styling
 */
export function PhoneInput({
  value,
  onChange,
  onComplete,
  error = false,
  helperText,
  label = 'رقم الهاتف',
  disabled = false,
}: PhoneInputProps) {
  // Parse phone into individual digits (excluding fixed 01 prefix)
  const parsePhone = (phone: string): string[] => {
    const clean = phone.replace(/[^0-9]/g, ''); // Remove non-digits
    const withoutPrefix = clean.startsWith('01') ? clean.slice(2) : clean;
    const digits = withoutPrefix.split('').slice(0, 9); // Max 9 digits after 01
    return [...digits, ...Array(9 - digits.length).fill('')];
  };

  const [digits, setDigits] = useState<string[]>(() => parsePhone(value));
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Update parent when digits change
  useEffect(() => {
    const digitString = digits.join('');
    const fullPhone = `01${digitString}`;
    onChange(fullPhone);

    if (digitString.length === 9 && onComplete) {
      onComplete(fullPhone);
    }
  }, [digits, onChange, onComplete]);

  // Update digits when value prop changes externally
  useEffect(() => {
    const newDigits = parsePhone(value);
    setDigits(newDigits);
  }, [value]);

  const handleChange = (index: number, newValue: string) => {
    // Only allow single digit
    const digit = newValue.replace(/[^0-9]/g, '').slice(-1);

    const newDigits = [...digits];
    newDigits[index] = digit;
    setDigits(newDigits);

    // Auto-focus next input
    if (digit && index < 8) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      // Move to previous input on backspace if current is empty
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowRight' && index < 8) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e: ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text');
    let pastedDigits = pastedData.replace(/[^0-9]/g, '').split('');

    // Remove 01 prefix if present
    if (pastedDigits[0] === '0' && pastedDigits[1] === '1') {
      pastedDigits = pastedDigits.slice(2);
    }

    pastedDigits = pastedDigits.slice(0, 9);

    const newDigits = [...pastedDigits, ...Array(9 - pastedDigits.length).fill('')];
    setDigits(newDigits);

    // Focus last filled input
    const lastFilledIndex = pastedDigits.length - 1;
    if (lastFilledIndex >= 0 && lastFilledIndex < 9) {
      inputRefs.current[lastFilledIndex]?.focus();
    }
  };

  return (
    <Box>
      {label && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {label}
        </Typography>
      )}

      <div
        style={{
          display: 'flex',
          gap: '2px',
          alignItems: 'center',
          flexWrap: 'wrap',
          direction: 'ltr',
          justifyContent: 'flex-start',
          maxWidth: '100%',
        }}
        onPaste={handlePaste}
        dir="ltr"
      >
        {/* 01 prefix */}
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            color: 'text.secondary',
            minWidth: 40,
            textAlign: 'center',
          }}
        >
          01
        </Typography>

        {/* 9 editable digits */}
        {digits.map((digit, idx) => (
          <TextField
            key={`digit-${idx}`}
            inputRef={(el) => (inputRefs.current[idx] = el)}
            value={digit}
            onChange={(e) => handleChange(idx, e.target.value)}
            onKeyDown={(e) => handleKeyDown(idx, e)}
            disabled={disabled}
            error={error}
            inputProps={{
              maxLength: 1,
              inputMode: 'numeric',
              pattern: '[0-9]*',
              dir: 'ltr',
              style: {
                textAlign: 'center',
                fontSize: '1rem',
                fontWeight: 500,
                padding: '6px 0',
                direction: 'ltr',
              },
            }}
            InputProps={{
              sx: {
                direction: 'ltr !important',
                '& input': {
                  direction: 'ltr !important',
                },
              },
            }}
            sx={{
              width: 32,
              direction: 'ltr !important',
              '& .MuiOutlinedInput-root': {
                direction: 'ltr !important',
                '& fieldset': {
                  border: 'none',
                  borderBottom: '2px solid',
                  borderBottomColor: error ? 'error.main' : 'divider',
                  borderRadius: 0,
                },
                '&:hover fieldset': {
                  borderBottomColor: error ? 'error.main' : 'primary.main',
                },
                '&.Mui-focused fieldset': {
                  borderBottomColor: error ? 'error.main' : 'primary.main',
                  borderBottomWidth: 2,
                },
              },
            }}
          />
        ))}
      </div>

      {helperText && (
        <Typography
          variant="caption"
          color={error ? 'error' : 'text.secondary'}
          sx={{ mt: 0.5, display: 'block' }}
        >
          {helperText}
        </Typography>
      )}
    </Box>
  );
}
